import React, { useState, useEffect } from 'react';
import { notificationApi } from '../services/api';
import { EmailCadence } from '../types';

const NotificationSettings: React.FC = () => {
  const [emailCadence, setEmailCadence] = useState<EmailCadence>('none');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Load current preferences when component mounts
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const preferences = await notificationApi.getPreferences();
      setEmailCadence(preferences.email_cadence);
    } catch (error: any) {
      console.error('Failed to load notification preferences:', error);
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setSaving(true);

    try {
      await notificationApi.updatePreferences({ email_cadence: emailCadence });
      setMessage('Notification preferences updated successfully!');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const getCadenceDescription = (cadence: EmailCadence): string => {
    switch (cadence) {
      case 'none':
        return 'You will not receive any email notifications';
      case 'immediate':
        return 'Get notified immediately when someone matches your available date';
      case 'weekly':
        return 'Receive a weekly digest of all your matches';
      case 'monthly':
        return 'Receive a monthly digest of all your matches';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="notification-settings">
        <h3>📧 Email Notifications</h3>
        <div className="loading">Loading preferences...</div>
      </div>
    );
  }

  return (
    <div className="notification-settings">
      <h3>📧 Email Notifications</h3>
      <p className="settings-description">
        Choose how you'd like to be notified when someone else selects a date you're already available for.
      </p>

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="notification-form">
        <div className="radio-group">
          <div className="radio-option">
            <label className={`radio-label ${emailCadence === 'none' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="emailCadence"
                value="none"
                checked={emailCadence === 'none'}
                onChange={(e) => setEmailCadence(e.target.value as EmailCadence)}
                disabled={saving}
              />
              <div className="radio-content">
                <div className="radio-title">🔕 No Email</div>
                <div className="radio-description">You will not receive any email notifications</div>
              </div>
            </label>
          </div>

          <div className="radio-option">
            <label className={`radio-label ${emailCadence === 'immediate' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="emailCadence"
                value="immediate"
                checked={emailCadence === 'immediate'}
                onChange={(e) => setEmailCadence(e.target.value as EmailCadence)}
                disabled={saving}
              />
              <div className="radio-content">
                <div className="radio-title">⚡ Immediate</div>
                <div className="radio-description">Get notified immediately when someone matches your available date</div>
              </div>
            </label>
          </div>

          <div className="radio-option">
            <label className={`radio-label ${emailCadence === 'weekly' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="emailCadence"
                value="weekly"
                checked={emailCadence === 'weekly'}
                onChange={(e) => setEmailCadence(e.target.value as EmailCadence)}
                disabled={saving}
              />
              <div className="radio-content">
                <div className="radio-title">📅 Weekly Digest</div>
                <div className="radio-description">Receive a weekly digest of all your matches</div>
              </div>
            </label>
          </div>

          <div className="radio-option">
            <label className={`radio-label ${emailCadence === 'monthly' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="emailCadence"
                value="monthly"
                checked={emailCadence === 'monthly'}
                onChange={(e) => setEmailCadence(e.target.value as EmailCadence)}
                disabled={saving}
              />
              <div className="radio-content">
                <div className="radio-title">📆 Monthly Digest</div>
                <div className="radio-description">Receive a monthly digest of all your matches</div>
              </div>
            </label>
          </div>
        </div>

        <div className="current-selection">
          <strong>Current setting:</strong> {getCadenceDescription(emailCadence)}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="save-button"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </form>

      <div className="notification-info">
        <h4>💡 How it works:</h4>
        <ul>
          <li><strong>Match Detection:</strong> When someone selects a date you're already available for, that creates a "match"</li>
          <li><strong>Email Only for Existing Dates:</strong> You only get notified if you selected the date first</li>
          <li><strong>Contact Information:</strong> Emails include the other person's contact details so you can coordinate</li>
          <li><strong>Weekly/Monthly Digests:</strong> Collect all matches and send them in a single email</li>
        </ul>
      </div>
    </div>
  );
};

export default NotificationSettings;